import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import axios, { AxiosError } from 'axios'
import GetUrls from 'ts/enums/Url'
import { User } from 'ts/types'
import { Input, CheckBoxToggle } from 'components/ui'
import ButtonBasic from 'components/ui/ButtonBasic'
import CONFIG from 'config/constants'
import ls from 'utils/ls'

interface PasswordChangeModalProps {
  user: User
  isOpen: boolean
  onClose: () => void
  onPasswordChangeSuccess?: (wasUnlocked: boolean) => void
}

interface PasswordChangeForm {
  temporaryPassword: boolean
  password: string
  adminPassword: string
  unlock: boolean
}

const PasswordChangeModal = ({ user, isOpen, onClose, onPasswordChangeSuccess }: PasswordChangeModalProps) => {
  const Url = GetUrls()
  const [isLoading, setIsLoading] = useState(false)
  const [isTemporaryPassword, setIsTemporaryPassword] = useState(false)
  const [unlockUser, setUnlockUser] = useState(false)
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<PasswordChangeForm>()

  const handlePasswordChange = async (data: PasswordChangeForm) => {
    setIsLoading(true)
    
    try {
      const token = ls.get(CONFIG.LSKEY_TOKEN)
      
      const payload = {
        programUserId: user.programUserId,
        newPassword: data.password,
        adminPassword: data.adminPassword,
        requireChange: data.temporaryPassword,
        unlock: data.unlock
      }

      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...(token && {
            'Authorization': `Bearer ${token}`,
            'X-API-Key': CONFIG.API_KEY
          })
        }
      }

      const response = await axios.post(Url.postChangeUserPassword, payload, config)

      if (response.status === 200 || response.status === 201) {
        toast.success('Password changed successfully!')
        
        // Call the success callback if provided, passing whether the user was unlocked
        if (onPasswordChangeSuccess) {
          onPasswordChangeSuccess(data.unlock)
        }
        
        reset()
        onClose()
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>
      
      if (axiosError.response?.data?.message) {
        toast.error(axiosError.response.data.message)
      } else if (axiosError.message) {
        toast.error(axiosError.message)
      } else {
        toast.error('An error occurred while changing the password')
      }
      
      console.error('Password change error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleTemporaryPassword = (isChecked: boolean) => {
    setIsTemporaryPassword(isChecked)
  }

  const handleToggleUnlock = (isChecked: boolean) => {
    setUnlockUser(isChecked)
  }

  const handleClose = () => {
    reset()
    setIsTemporaryPassword(false)
    setUnlockUser(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Password and Account Recovery</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit(handlePasswordChange)} className="space-y-4">
          <CheckBoxToggle
            defaultChecked={isTemporaryPassword}
            label="Require Password Change"
            onToggle={handleToggleTemporaryPassword}
          />
          
          <CheckBoxToggle
            defaultChecked={unlockUser}
            label="Reset Invalid Logins"
            onToggle={handleToggleUnlock}
          />
          
          <Input
            label="New Password"
            type="password"
            {...register('password', { 
              required: 'Password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters'
              }
            })}
          />
          {errors.password && (
            <p className="text-red-500 text-sm">{errors.password.message}</p>
          )}
          
          <Input
            label="Admin Password"
            type="password"
            {...register('adminPassword', { 
              required: 'Admin password is required' 
            })}
          />
          {errors.adminPassword && (
            <p className="text-red-500 text-sm">{errors.adminPassword.message}</p>
          )}
          
          <input
            type="hidden"
            {...register('temporaryPassword', { value: isTemporaryPassword })}
          />
          
          <input
            type="hidden"
            {...register('unlock', { value: unlockUser })}
          />
          
          <div className="flex gap-2 pt-4">

            <ButtonBasic
              type="submit"
              className="flex-1"
              isLoading={isLoading}
            >
              Confirm
            </ButtonBasic>
            <ButtonBasic
              type="button"
              onClick={handleClose}
              className="flex-1 bg-transparent-force border border-indigo-500 text-indigo-500"
              disabled={isLoading}
              isLoading={false}
            >
              Cancel
            </ButtonBasic>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PasswordChangeModal